import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import {
  Users, FileText, CheckCircle2, AlertTriangle, Gavel, Search, DollarSign,
  Mail, MailX, BarChart3, ChevronRight, X, AlertCircle, ArrowUpRight, Loader2,
  BookUser, Save,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip } from "recharts";

const TABS = [
  { key: "summary", label: "Resumen", icon: BarChart3 },
  { key: "users", label: "Usuarios", icon: Users },
  { key: "payments", label: "Pagos", icon: DollarSign },
  { key: "scans", label: "Escaneos", icon: Search },
  { key: "emails", label: "Correos", icon: Mail },
  { key: "directory", label: "Directorio", icon: BookUser },
];

export default function Admin() {
  const [tab, setTab] = useState("summary");
  return (
    <div className="bg-[#F9F9F8] min-h-screen">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="eyebrow mb-2">Sala de control</div>
            <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight text-gray-900">
              Super Admin · borra-me.com
            </h1>
          </div>
          <div className="text-xs font-mono text-gray-500">
            Acceso restringido · solo rol <code className="text-[#E63946]">admin</code>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 border-b border-gray-300 mt-8" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`px-4 py-3 text-sm font-mono uppercase tracking-[0.12em] transition-colors flex items-center gap-2 -mb-px border-b-2 ${
                tab === t.key
                  ? "border-[#E63946] text-[#0B2447] font-bold"
                  : "border-transparent text-gray-500 hover:text-gray-900"
              }`}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "summary" && <SummaryTab />}
          {tab === "users" && <UsersTab />}
          {tab === "payments" && <PaymentsTab />}
          {tab === "scans" && <ScansTab />}
          {tab === "emails" && <EmailsTab />}
          {tab === "directory" && <DirectoryTab />}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SUMMARY
// ---------------------------------------------------------------------------

function SummaryTab() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/admin/metrics").then((r) => setM(r.data)); }, []);
  if (!m) return <Loading />;
  return (
    <>
      <EmailStatusBanner live={m.broker_emails_live} configured={m.resend_configured} />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-gray-200 border border-gray-200">
        <Stat icon={Users} label="Usuarios" v={m.users_total} sub={`+${m.signups_recent} en 14d`} testid="m-users" />
        <Stat icon={CheckCircle2} label="Suscritos" v={m.subscribed} color="text-green-600" testid="m-subscribed" />
        <Stat icon={DollarSign} label="Ingresos" v={formatCLP(m.revenue_total)} sub={`${m.payments_count} pagos`} color="text-[#0B2447]" testid="m-revenue" />
        <Stat icon={Search} label="Escaneos" v={m.scans_total} testid="m-scans" />
        <Stat icon={FileText} label="Solicitudes" v={m.requests_total} testid="m-req" />
        <Stat icon={Gavel} label="Denuncias" v={m.denuncia_ready} color="text-red-600" testid="m-den" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-200 border border-gray-200 mt-px">
        <Stat icon={CheckCircle2} label="ARCOP eliminados" v={m.deleted} color="text-green-600" testid="m-del" />
        <Stat icon={AlertTriangle} label="ARCOP escalados" v={m.escalated} color="text-amber-600" testid="m-esc" />
        <Stat icon={Mail} label="Correos enviados" v={m.email_total} testid="m-emails" />
        <Stat icon={MailX} label="Correos fallidos" v={m.email_failed} color={m.email_failed > 0 ? "text-red-600" : "text-gray-900"} testid="m-emails-fail" />
      </div>

      <div className="grid lg:grid-cols-12 gap-8 mt-10">
        <div className="lg:col-span-7 bg-white border border-gray-300 p-6">
          <div className="eyebrow mb-4">Performance por broker</div>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={m.broker_stats}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#6B7280" }} angle={-30} textAnchor="end" height={70} />
              <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} />
              <Tooltip />
              <Bar dataKey="requests" fill="#0B2447" />
              <Bar dataKey="deleted" fill="#16A34A" />
              <Bar dataKey="escalated" fill="#EA580C" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="lg:col-span-5 bg-white border border-gray-300 p-6">
          <div className="eyebrow mb-4">Detalle por broker</div>
          <div className="divide-y divide-gray-100 max-h-[320px] overflow-y-auto">
            {m.broker_stats.map((b) => (
              <div key={b.name} className="flex items-center justify-between py-3">
                <div>
                  <div className="font-mono text-sm">{b.name}</div>
                  <div className="text-xs text-gray-500">Tier {b.tier}</div>
                </div>
                <div className="flex items-center gap-4 text-xs font-mono tabular-nums">
                  <span>{b.requests} req</span>
                  <span className="text-green-600">{b.deleted} del</span>
                  <span className="text-amber-600">{b.escalated} esc</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// USERS
// ---------------------------------------------------------------------------

function UsersTab() {
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const load = useCallback(() => {
    api.get(`/admin/users?limit=50&q=${encodeURIComponent(q)}`).then((r) => setData(r.data));
  }, [q]);
  useEffect(() => { load(); }, [load]);
  const openDetail = (uid) => api.get(`/admin/users/${uid}`).then((r) => setDetail(r.data));

  return (
    <>
      <div className="flex items-center gap-3 mb-4">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Buscar por email, nombre o RUT…"
          data-testid="admin-users-search"
          className="flex-1 max-w-md border border-gray-300 px-4 py-2 text-sm focus:border-[#0B2447] focus:outline-none"
        />
        <button onClick={load} className="px-4 py-2 bg-[#0B2447] hover:bg-[#071932] text-white text-sm font-semibold" data-testid="admin-users-search-btn">
          Buscar
        </button>
      </div>
      {!data ? <Loading /> : (
        <div className="bg-white border border-gray-300 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-200 eyebrow flex items-center justify-between">
            <span>{data.total} usuarios</span>
            <span className="text-gray-400">Click para ver detalle</span>
          </div>
          <div className="divide-y divide-gray-100">
            {data.items.map((u) => (
              <button key={u.id} onClick={() => openDetail(u.id)}
                data-testid={`admin-user-row-${u.id}`}
                className="w-full grid grid-cols-12 gap-3 items-center py-3 px-5 hover:bg-gray-50 transition-colors text-left">
                <div className="col-span-4 min-w-0">
                  <div className="text-sm font-mono text-gray-900 truncate">{u.email}</div>
                  <div className="text-xs text-gray-500 truncate">{u.nombre || "—"}</div>
                </div>
                <div className="col-span-2 text-xs">
                  {u.role === "admin" ? (
                    <span className="bg-[#E63946] text-white px-2 py-0.5 font-mono uppercase">admin</span>
                  ) : u.subscribed ? (
                    <span className="bg-green-600 text-white px-2 py-0.5 font-mono uppercase">suscrito</span>
                  ) : (
                    <span className="text-gray-500 font-mono">trial</span>
                  )}
                </div>
                <div className="col-span-2 text-xs font-mono text-gray-600 truncate">{u.rut || "—"}</div>
                <div className="col-span-2 text-xs font-mono text-gray-600">
                  {u.scans_count}s · {u.requests_count}r
                </div>
                <div className="col-span-2 text-xs font-mono text-gray-500 text-right">
                  {(u.created_at || "").slice(0, 10)} <ChevronRight className="inline w-3 h-3" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      {detail && <UserDetailModal data={detail} onClose={() => setDetail(null)} />}
    </>
  );
}

function UserDetailModal({ data, onClose }) {
  const u = data.user || {};
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center sm:justify-center p-0 sm:p-6" onClick={onClose} data-testid="admin-user-detail">
      <div className="bg-white w-full sm:max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-200 p-5 flex items-start justify-between">
          <div>
            <div className="eyebrow text-red-600">Usuario</div>
            <div className="font-display font-bold text-lg text-gray-900">{u.email}</div>
            <div className="text-xs font-mono text-gray-500 mt-1">
              {u.nombre || "—"} · {u.rut || "sin RUT"} · {u.role || "user"} · {u.subscribed ? "suscrito" : "no suscrito"}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-900 p-2"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-6">
          <KV label="Plan" v={u.plan || "—"} />
          <KV label="Onboarded" v={u.onboarded ? "Sí" : "No"} />
          <KV label="Teléfono" v={u.telefono || "—"} />
          <KV label="Dirección" v={u.direccion || "—"} />
          <KV label="Comuna · Región" v={`${u.comuna || "—"} · ${u.region || "—"}`} />
          <KV label="Creado" v={u.created_at} />

          <Block title={`Escaneos (${(data.scans || []).length})`}>
            {(data.scans || []).map((s) => (
              <div key={s.id} className="flex justify-between text-xs font-mono py-1.5 border-b border-gray-100">
                <span>#{s.id?.slice(0, 8)} · score {s.score}</span>
                <span className="text-gray-500">{(s.created_at || "").slice(0, 16).replace("T", " ")}</span>
              </div>
            ))}
            {!data.scans?.length && <Empty />}
          </Block>

          <Block title={`Solicitudes ARCOP (${(data.requests || []).length})`}>
            {(data.requests || []).map((r) => (
              <div key={r.id} className="flex justify-between text-xs font-mono py-1.5 border-b border-gray-100">
                <span className="truncate">{r.broker_name} → <span className="text-gray-500">{r.status}</span></span>
                <span className="text-gray-500 shrink-0">{(r.sent_at || r.created_at || "").slice(0, 10)}</span>
              </div>
            ))}
            {!data.requests?.length && <Empty />}
          </Block>

          <Block title={`Pagos (${(data.payments || []).length})`}>
            {(data.payments || []).map((p, i) => (
              <div key={i} className="flex justify-between text-xs font-mono py-1.5 border-b border-gray-100">
                <span>{p.plan || "—"} · {p.status}</span>
                <span className="text-gray-500">{formatCLP(p.amount)} · {(p.created_at || "").slice(0, 10)}</span>
              </div>
            ))}
            {!data.payments?.length && <Empty />}
          </Block>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PAYMENTS
// ---------------------------------------------------------------------------

function PaymentsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/payments?limit=80").then((r) => setData(r.data)); }, []);
  if (!data) return <Loading />;
  return (
    <div className="bg-white border border-gray-300">
      <div className="px-5 py-3 border-b border-gray-200 eyebrow">{data.total} pagos · Webpay Transbank</div>
      {data.items.length === 0 ? <Empty /> : (
        <div className="divide-y divide-gray-100">
          {data.items.map((p, i) => (
            <div key={i} className="grid grid-cols-12 gap-3 py-3 px-5 text-xs font-mono" data-testid={`admin-payment-row-${i}`}>
              <div className="col-span-3 truncate">{p.user_email || "—"}</div>
              <div className="col-span-2 text-gray-600 truncate">{p.user_nombre || "—"}</div>
              <div className="col-span-2 truncate">{p.plan || "—"}</div>
              <div className="col-span-2">
                <span className={`px-2 py-0.5 uppercase ${
                  ["approved", "paid", "completed"].includes(p.status) ? "bg-green-100 text-green-700" :
                  ["pending"].includes(p.status) ? "bg-amber-100 text-amber-700" :
                  "bg-red-100 text-red-700"
                }`}>{p.status || "—"}</span>
              </div>
              <div className="col-span-2 tabular-nums">{formatCLP(p.amount)}</div>
              <div className="col-span-1 text-gray-500 text-right">{(p.created_at || "").slice(5, 10)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SCANS
// ---------------------------------------------------------------------------

function ScansTab() {
  const [data, setData] = useState(null);
  const [rut, setRut] = useState("");
  const [detail, setDetail] = useState(null);
  const load = useCallback(() => {
    api.get(`/admin/scans?limit=50&rut=${encodeURIComponent(rut)}`).then((r) => setData(r.data));
  }, [rut]);
  useEffect(() => { load(); }, [load]);
  const openDetail = (sid) => api.get(`/admin/scans/${sid}`).then((r) => setDetail(r.data));
  return (
    <>
      <div className="flex items-center gap-3 mb-4">
        <input value={rut} onChange={(e) => setRut(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Filtrar por RUT (ej. 14.082.825)"
          className="flex-1 max-w-md border border-gray-300 px-4 py-2 text-sm focus:border-[#0B2447] focus:outline-none font-mono"
          data-testid="admin-scans-search" />
        <button onClick={load} className="px-4 py-2 bg-[#0B2447] hover:bg-[#071932] text-white text-sm font-semibold">Buscar</button>
      </div>
      {!data ? <Loading /> : (
        <div className="bg-white border border-gray-300">
          <div className="px-5 py-3 border-b border-gray-200 eyebrow">{data.total} escaneos</div>
          {data.items.length === 0 ? <Empty /> : (
            <div className="divide-y divide-gray-100">
              {data.items.map((s) => (
                <button key={s.id} onClick={() => openDetail(s.id)}
                  data-testid={`admin-scan-row-${s.id}`}
                  className="w-full grid grid-cols-12 gap-3 py-3 px-5 hover:bg-gray-50 transition-colors text-left text-xs font-mono">
                  <div className="col-span-3 truncate">{s.nombre || "—"}</div>
                  <div className="col-span-3 tabular-nums">{s.rut}</div>
                  <div className="col-span-2">Score <b>{s.score}</b></div>
                  <div className="col-span-2">{s.with_data_count} con datos</div>
                  <div className="col-span-2 text-gray-500 text-right">{(s.created_at || "").slice(0, 16).replace("T", " ")} <ChevronRight className="inline w-3 h-3" /></div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {detail && <ScanDetailModal data={detail} onClose={() => setDetail(null)} />}
    </>
  );
}

function ScanDetailModal({ data, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center sm:justify-center p-0 sm:p-6" onClick={onClose} data-testid="admin-scan-detail">
      <div className="bg-white w-full sm:max-w-4xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-200 p-5 flex items-start justify-between z-10">
          <div>
            <div className="eyebrow text-red-600">Escaneo #{data.id?.slice(0, 8)}</div>
            <div className="font-display font-bold text-lg text-gray-900">{data.nombre}</div>
            <div className="text-xs font-mono text-gray-500 mt-1">{data.rut} · score {data.score} · {(data.created_at || "").slice(0, 19).replace("T", " ")}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-900 p-2"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="eyebrow mb-3">Findings · {(data.findings || []).length}</div>
          {(data.findings || []).map((f) => {
            const ev = f.evidence || {};
            const extracted = ev.extracted_data;
            const exposedFields = ev.exposed_fields;
            const statusColor = ev.status === "confirmed" ? "text-red-600 bg-red-50 border-red-200" :
              ev.status === "exposed" ? "text-orange-600 bg-orange-50 border-orange-200" :
              ev.status === "blocked" ? "text-amber-600 bg-amber-50 border-amber-200" :
              ev.status === "clean" ? "text-green-600 bg-green-50 border-green-200" :
              "text-gray-500 bg-gray-50 border-gray-200";
            return (
              <div key={f.slug} className={`border ${statusColor.split(" ").slice(-1)[0]} bg-white`}>
                <div className="grid grid-cols-12 gap-3 px-4 py-3 items-center">
                  <div className="col-span-4">
                    <div className="font-mono text-sm font-semibold text-gray-900">{f.name}</div>
                    <div className="text-[10px] font-mono text-gray-500 mt-0.5">Tier {f.tier} · {f.method}</div>
                  </div>
                  <div className="col-span-2">
                    <span className={`text-[10px] uppercase tracking-[0.12em] font-bold px-2 py-1 ${statusColor}`}>
                      {ev.status || "—"}
                    </span>
                  </div>
                  <div className="col-span-3 text-[10px] font-mono text-gray-500">
                    {ev.http_status != null && <>HTTP {ev.http_status}{ev.response_time_ms ? ` · ${ev.response_time_ms}ms` : ""}</>}
                  </div>
                  <div className="col-span-3 text-[10px] font-mono text-gray-500 truncate">
                    {(f.data_types || []).join(", ")}
                  </div>
                </div>
                {(extracted || exposedFields) && (
                  <div className="bg-red-50/40 border-t border-red-200/60 px-4 py-3 text-xs">
                    <div className="eyebrow text-red-700 mb-2">
                      {extracted ? `Datos personales extraídos del sitio` : `Campos expuestos detectados`}
                    </div>
                    {extracted ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                        {Object.entries(extracted).map(([k, v]) => (
                          <div key={k} className="flex gap-2 items-baseline">
                            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-red-700/70 shrink-0 w-20">{k}</span>
                            <span className="text-gray-900 font-mono break-all">{String(v)}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {exposedFields.map((label) => (
                          <span key={label} className="text-[10px] uppercase tracking-[0.12em] font-bold bg-white border border-red-300 text-red-700 px-2 py-1">
                            {label}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {ev.notes && (
                  <div className="px-4 py-2 text-[10px] font-mono text-gray-500 italic border-t border-gray-100">{ev.notes}</div>
                )}
                {ev.url && (
                  <div className="px-4 py-2 border-t border-gray-100">
                    <a href={ev.url} target="_blank" rel="noreferrer" className="text-[10px] font-mono text-blue-600 hover:underline inline-flex items-center gap-1">
                      Ver evidencia <ArrowUpRight className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EMAILS
// ---------------------------------------------------------------------------

function EmailsTab() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  useEffect(() => {
    const url = filter ? `/admin/emails?limit=100&status=${filter}` : "/admin/emails?limit=100";
    api.get(url).then((r) => setData(r.data));
  }, [filter]);
  return (
    <>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <FilterPill label="Todos" active={!filter} onClick={() => setFilter("")} />
        <FilterPill label="Enviados" active={filter === "sent"} onClick={() => setFilter("sent")} />
        <FilterPill label="Entregados" active={filter === "delivered"} onClick={() => setFilter("delivered")} />
        <FilterPill label="Fallidos" active={filter === "failed"} onClick={() => setFilter("failed")} />
      </div>
      {!data ? <Loading /> : (
        <div className="bg-white border border-gray-300">
          <div className="px-5 py-3 border-b border-gray-200 eyebrow flex items-center justify-between">
            <span>{data.total} envíos · {data.events?.length || 0} eventos</span>
            <span className="text-gray-400 text-[10px]">Resend · ARCOP + Recordatorios</span>
          </div>
          {(data.events || []).length === 0 ? <Empty /> : (
            <div className="divide-y divide-gray-100">
              {data.events.map((e, i) => (
                <div key={i} className="grid grid-cols-12 gap-3 py-3 px-5 text-xs font-mono items-start" data-testid={`admin-email-row-${i}`}>
                  <div className="col-span-2">
                    <span className={
                      e.kind === "reminder_d5" ? "bg-amber-100 text-amber-700 px-2 py-0.5 uppercase" :
                      "bg-[#0B2447] text-white px-2 py-0.5 uppercase"
                    }>{e.kind === "reminder_d5" ? "D+5" : "ARCOP"}</span>
                  </div>
                  <div className="col-span-3 truncate">{e.broker_name}</div>
                  <div className="col-span-3 truncate text-gray-500">{e.broker_email || "—"}</div>
                  <div className="col-span-2">
                    <span className={
                      e.status === "delivered" ? "bg-green-100 text-green-700 px-2 py-0.5 uppercase" :
                      e.status === "sent" ? "bg-blue-100 text-blue-700 px-2 py-0.5 uppercase" :
                      e.status === "failed" ? "bg-red-100 text-red-700 px-2 py-0.5 uppercase" :
                      "bg-gray-100 text-gray-700 px-2 py-0.5 uppercase"
                    }>{e.status || "—"}</span>
                    {e.error && <div className="text-[10px] text-red-700 mt-1 flex items-start gap-1"><AlertCircle className="w-3 h-3 mt-0.5 shrink-0" /> {e.error.slice(0, 60)}</div>}
                  </div>
                  <div className="col-span-2 text-gray-500 text-right text-[10px]">{(e.sent_at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// UTILITIES
// ---------------------------------------------------------------------------

function Stat({ icon: Icon, label, v, sub, color = "text-gray-900", testid }) {
  return (
    <div className="bg-white p-5" data-testid={testid}>
      <Icon className="w-4 h-4 text-gray-400 mb-3" />
      <div className="eyebrow mb-1">{label}</div>
      <div className={`font-display text-2xl font-bold ${color} tabular-nums`}>{v}</div>
      {sub && <div className="text-[10px] font-mono text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

function KV({ label, v }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 border-b border-dashed border-gray-200">
      <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400">{label}</span>
      <span className="text-sm font-mono text-gray-900 truncate">{v || "—"}</span>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div>
      <div className="eyebrow mb-2">{title}</div>
      <div>{children}</div>
    </div>
  );
}

function Empty() {
  return <div className="text-xs font-mono text-gray-400 py-6 text-center">Sin registros.</div>;
}

function Loading() {
  return (
    <div className="flex items-center gap-2 text-sm font-mono text-gray-500 py-12 justify-center">
      <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
    </div>
  );
}

function FilterPill({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-xs font-mono uppercase tracking-[0.12em] border ${
        active ? "bg-[#0B2447] text-white border-[#0B2447]" : "bg-white text-gray-700 border-gray-300 hover:border-[#0B2447]"
      }`}
    >
      {label}
    </button>
  );
}

function EmailStatusBanner({ live, configured }) {
  if (live && configured) {
    return (
      <div className="mb-6 border-l-4 border-green-600 bg-green-50 px-4 py-3 flex items-start gap-3" data-testid="admin-emails-status-live">
        <CheckCircle2 className="w-4 h-4 text-green-700 mt-0.5 shrink-0" />
        <div className="text-xs text-green-900 leading-relaxed">
          <b>Envío de correos a brokers: ACTIVO.</b> Cada dispatch de un usuario suscrito (no-admin) genera
          un email real vía Resend. El super admin sigue bloqueado: nunca dispara correos reales.
        </div>
      </div>
    );
  }
  return (
    <div className="mb-6 border-l-4 border-amber-500 bg-amber-50 px-4 py-3 flex items-start gap-3" data-testid="admin-emails-status-paused">
      <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
      <div className="text-xs text-amber-900 leading-relaxed">
        <b>Envío de correos a brokers: PAUSADO (dry-run global).</b>{" "}
        {configured
          ? "Resend está configurado, pero BROKER_EMAILS_LIVE=false."
          : "Resend NO está configurado."}{" "}
        Los dispatch se persisten en DB como <code>delivery.status=dry_run</code> sin llamar a Resend.
        Para activar: setear <code className="bg-amber-100 px-1">BROKER_EMAILS_LIVE=true</code> en
        las variables de entorno y reiniciar el backend.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DIRECTORIO — Contacto editable de brokers y empresas
// ---------------------------------------------------------------------------
function DirectoryTab() {
  const [subTab, setSubTab] = useState("empresa");
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setRows(null);
    setErr(null);
    try {
      const { data } = await api.get(`/admin/entities?entity_type=${subTab}`);
      setRows(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Error al cargar el directorio");
    }
  }, [subTab]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="eyebrow">Directorio de contacto</div>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            Emails, teléfonos y comentarios de los brokers y empresas. Estos son los datos que se
            usan al despachar las solicitudes ARCOP. Edita en línea y guarda por fila.
          </p>
        </div>
        <div className="inline-flex border border-gray-300 bg-white" data-testid="dir-sub-tabs">
          <button
            onClick={() => setSubTab("broker")}
            data-testid="dir-tab-broker"
            className={`px-4 py-2 text-xs uppercase tracking-[0.12em] font-bold ${subTab === "broker" ? "bg-[#0B2447] text-white" : "text-gray-700 hover:bg-gray-50"}`}
          >
            Brokers
          </button>
          <button
            onClick={() => setSubTab("empresa")}
            data-testid="dir-tab-empresa"
            className={`px-4 py-2 text-xs uppercase tracking-[0.12em] font-bold ${subTab === "empresa" ? "bg-[#0B2447] text-white" : "text-gray-700 hover:bg-gray-50"}`}
          >
            Empresas
          </button>
        </div>
      </div>

      {err && (
        <div className="mb-3 flex items-start gap-2 bg-red-50 border border-red-200 text-red-800 text-sm p-3" data-testid="dir-err">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />{err}
        </div>
      )}

      {rows === null && !err && (
        <div className="bg-white border border-gray-200 py-16 text-center">
          <Loader2 className="w-6 h-6 text-gray-400 animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-600">Cargando directorio…</p>
        </div>
      )}

      {rows && rows.length === 0 && (
        <div className="bg-white border border-gray-200 py-16 text-center text-sm text-gray-600">
          No hay registros para este tipo.
        </div>
      )}

      {rows && rows.length > 0 && (
        <div className="space-y-px bg-gray-200 border border-gray-200" data-testid="dir-list">
          <div className="hidden md:grid grid-cols-[minmax(160px,1fr)_minmax(220px,1.5fr)_minmax(220px,1.5fr)_minmax(140px,0.9fr)_minmax(220px,1.5fr)_100px] gap-3 bg-[#F5F5F4] px-4 py-2 text-[10px] uppercase tracking-[0.15em] font-bold text-gray-600">
            <div>Nombre</div>
            <div>Email principal</div>
            <div>CC (separar con coma)</div>
            <div>Teléfono</div>
            <div>Notas</div>
            <div className="text-right">Acción</div>
          </div>
          {rows.map((r) => (
            <DirectoryRow key={r.slug} row={r} onSaved={(updated) => {
              setRows(rows.map(x => x.slug === updated.slug ? { ...x, ...updated } : x));
            }}/>
          ))}
        </div>
      )}
    </div>
  );
}

function DirectoryRow({ row, onSaved }) {
  const [contact_email, setEmail] = useState(row.contact_email || "");
  const [ccText, setCcText] = useState((row.cc_emails || []).join(", "));
  const [phone, setPhone] = useState(row.phone || "");
  const [notes, setNotes] = useState(row.notes || "");
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null); // 'ok' | 'err'
  const [errMsg, setErrMsg] = useState(null);

  const dirty = contact_email !== (row.contact_email || "")
    || ccText !== ((row.cc_emails || []).join(", "))
    || phone !== (row.phone || "")
    || notes !== (row.notes || "");

  const save = async () => {
    setSaving(true);
    setErrMsg(null);
    const cc_emails = ccText
      .split(",")
      .map(s => s.trim())
      .filter(s => s && s.includes("@"));
    try {
      const { data } = await api.patch(`/admin/entities/${row.slug}`, {
        contact_email: contact_email || null,
        cc_emails,
        phone,
        notes,
      });
      onSaved({
        slug: row.slug,
        contact_email,
        cc_emails,
        phone,
        notes,
      });
      setFlash("ok");
      setTimeout(() => setFlash(null), 1500);
      void data;
    } catch (e) {
      setErrMsg(
        typeof e.response?.data?.detail === "string"
          ? e.response.data.detail
          : "No se pudo guardar. Verifica los emails ingresados."
      );
      setFlash("err");
      setTimeout(() => setFlash(null), 2500);
    } finally {
      setSaving(false);
    }
  };

  const borderClass =
    flash === "ok"  ? "border-l-4 border-l-green-500"
    : flash === "err" ? "border-l-4 border-l-red-500"
    : "border-l-4 border-l-transparent";

  return (
    <div
      data-testid={`dir-row-${row.slug}`}
      className={`bg-white p-4 md:grid md:grid-cols-[minmax(160px,1fr)_minmax(220px,1.5fr)_minmax(220px,1.5fr)_minmax(140px,0.9fr)_minmax(220px,1.5fr)_100px] md:gap-3 md:items-start ${borderClass} transition-colors`}
    >
      <div className="mb-2 md:mb-0">
        <div className="font-semibold text-sm text-gray-900">{row.name}</div>
        <div className="text-[11px] font-mono text-gray-500 mt-0.5">
          {row.entity_type === "empresa"
            ? row.category
            : `Tier ${row.tier || "?"}`}
          {row.spam_score ? ` · spam ${row.spam_score}/5` : ""}
        </div>
      </div>
      <input
        type="email"
        value={contact_email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="dpo@empresa.cl"
        data-testid={`dir-email-${row.slug}`}
        className="w-full border border-gray-300 px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0B2447]/40 mb-2 md:mb-0"
      />
      <input
        type="text"
        value={ccText}
        onChange={(e) => setCcText(e.target.value)}
        placeholder="legal@empresa.cl, compliance@empresa.cl"
        data-testid={`dir-cc-${row.slug}`}
        className="w-full border border-gray-300 px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0B2447]/40 mb-2 md:mb-0"
      />
      <input
        type="text"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder="+56 2 2xxx xxxx"
        data-testid={`dir-phone-${row.slug}`}
        className="w-full border border-gray-300 px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0B2447]/40 mb-2 md:mb-0"
      />
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Comentario interno (validaciones, contactos alternos, etc.)"
        data-testid={`dir-notes-${row.slug}`}
        rows={2}
        className="w-full border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B2447]/40 mb-2 md:mb-0 resize-none"
      />
      <div className="flex flex-col items-stretch md:items-end gap-1">
        <button
          onClick={save}
          disabled={!dirty || saving}
          data-testid={`dir-save-${row.slug}`}
          className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.1em] transition-colors
            ${dirty && !saving ? "bg-[#0B2447] text-white hover:bg-[#071932]" : "bg-gray-200 text-gray-400 cursor-not-allowed"}`}
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {saving ? "…" : "Guardar"}
        </button>
        {flash === "ok" && (
          <span className="text-[10px] font-mono uppercase tracking-wider text-green-700" data-testid={`dir-ok-${row.slug}`}>
            ✓ guardado
          </span>
        )}
        {errMsg && (
          <span className="text-[10px] font-mono text-red-700 leading-tight" data-testid={`dir-err-${row.slug}`}>
            {errMsg}
          </span>
        )}
      </div>
    </div>
  );
}


function formatCLP(n) {
  if (typeof n !== "number") return "—";
  return "$" + n.toLocaleString("es-CL");
}
